import React, { useState, useEffect } from 'react';

export default function CreateCategoryModal({ isOpen, onClose, onCreate, onUpdate, categories, isDarkMode, initialData }) {
    const [name, setName] = useState('');
    const [slug, setSlug] = useState('');
    const [description, setDescription] = useState('');
    const [image, setImage] = useState(null);
    const [parentId, setParentId] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (initialData) {
            setName(initialData.name || '');
            setSlug(initialData.slug || '');
            setDescription(initialData.description || '');
            setParentId(initialData.parentId || '');
            setIsActive(initialData.isActive ?? true);
            setImage(null); // Reset image input
        } else {
            // Reset for create mode
            setName('');
            setSlug('');
            setDescription('');
            setParentId('');
            setIsActive(true);
            setImage(null);
        }
    }, [initialData, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('name', name);
            if (slug) formData.append('slug', slug);
            formData.append('description', description);
            formData.append('isActive', isActive);
            if (parentId) formData.append('parentId', parentId);
            if (image) formData.append('image', image);

            if (initialData) {
                // Update Mode
                await onUpdate(initialData.id, formData);
            } else {
                // Create Mode
                await onCreate(formData);
            }

            // Form cleanup happens via useEffect when initialData changes or modal re-opens
            onClose();
        } catch (error) {
            console.error(error);
            alert("Failed to save category");
        }
        setLoading(false);
    };

    const styles = getStyles(isDarkMode);

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <div style={styles.header}>
                    <h3>{initialData ? 'Edit Category' : 'Create New Category'}</h3>
                    <button onClick={onClose} style={styles.closeBtn}>&times;</button>
                </div>
                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Category Name *</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            style={styles.input}
                            placeholder="e.g., Starters"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Slug (Optional)</label>
                        <input
                            type="text"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            style={styles.input}
                            placeholder="e.g., starters-items"
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Parent Category (Optional)</label>
                        <select
                            value={parentId}
                            onChange={(e) => setParentId(e.target.value)}
                            style={styles.input}
                        >
                            <option value="">No Parent (Root Category)</option>
                            {categories
                                .filter(cat => cat.id !== (initialData?.id)) // Prevent self-parenting
                                .map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                        </select>
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            style={styles.textarea}
                            placeholder="Optional description..."
                        />
                    </div>

                    <div style={styles.formGroup}>
                        <label style={styles.label}>Category Image {initialData && '(Leave empty to keep current)'}</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setImage(e.target.files[0])}
                            style={styles.input}
                        />
                    </div>

                    <div style={styles.checkboxGroup}>
                        <input
                            type="checkbox"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            id="isActive"
                        />
                        <label htmlFor="isActive" style={styles.label}>Is Active</label>
                    </div>

                    <div style={styles.actions}>
                        <button type="button" onClick={onClose} style={styles.cancelBtn}>
                            Cancel
                        </button>
                        <button type="submit" disabled={loading} style={styles.submitBtn}>
                            {loading ? 'Saving...' : (initialData ? 'Save Changes' : 'Create Category')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const getStyles = (isDark) => ({
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    },
    modal: {
        backgroundColor: isDark ? '#2a2a2a' : '#fff',
        padding: '2rem',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '500px',
        color: isDark ? '#fff' : '#333',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem'
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        color: isDark ? '#aaa' : '#666',
        fontSize: '1.5rem',
        cursor: 'pointer'
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem'
    },
    checkboxGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
    },
    label: {
        fontSize: '0.9rem',
        fontWeight: '500',
        color: isDark ? '#ccc' : '#555'
    },
    input: {
        padding: '0.75rem',
        borderRadius: '4px',
        border: isDark ? '1px solid #444' : '1px solid #ddd',
        backgroundColor: isDark ? '#333' : '#fff',
        color: isDark ? '#fff' : '#333',
        fontSize: '1rem'
    },
    textarea: {
        padding: '0.75rem',
        borderRadius: '4px',
        border: isDark ? '1px solid #444' : '1px solid #ddd',
        backgroundColor: isDark ? '#333' : '#fff',
        color: isDark ? '#fff' : '#333',
        fontSize: '1rem',
        minHeight: '80px',
        resize: 'vertical'
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '1rem',
        marginTop: '1rem'
    },
    cancelBtn: {
        padding: '0.75rem 1.5rem',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: isDark ? '#444' : '#eee',
        color: isDark ? '#fff' : '#333',
        cursor: 'pointer'
    },
    submitBtn: {
        padding: '0.75rem 1.5rem',
        border: 'none',
        borderRadius: '4px',
        backgroundColor: '#0F9D58',
        color: '#fff',
        cursor: 'pointer',
        fontWeight: '500'
    }
});
